<template>
  <div class="case-actions">
    <div
      v-for="action in availableActions"
      :key="action.id"
      class="action"
      :class="{warning: actionWarnings(action.id).length, error: actionErrors(action.id).length}"
    >
      <div class="action-content">
        <label>
          {{ action.name.en }}
          <help-tooltip v-if="actionErrors(action.id).length" type="error">
            <span v-for="error in actionErrors(action.id)" :key="error.message">
              {{ error.message }}
              <br>
            </span>
          </help-tooltip>
          <help-tooltip v-if="actionWarnings(action.id).length" type="warning">
            <span v-for="warning in actionWarnings(action.id)" :key="warning.message">
              {{ warning.message }}
              <br>
            </span>
          </help-tooltip>
        </label>
        <toggle
          :on="isEnabled(action.id)"
          :disabled="readonly || isDisabled(action.id)"
          :on-color="color"
          @toggle="toggle(action.id)"
        />
        <slot v-if="isEnabled(action.id)" :name="`toggle-enabled-${action.id}`" />
        <slot v-if="isDisabled(action.id)" :name="`toggle-disabled-${action.id}`" />
        <div v-if="hasComponents(action)" class="components-container">
          <div
            v-for="component in action.components"
            :key="component.key"
            class="component"          
            :class="[component.type, { 
              error: componentErrors(action.id, component.key).length,
              warning: componentWarnings(action.id, component.key).length 
            }]"
          >
            <div class="componenent-content">
              <label>
                {{ component.label.en }}            
                <help-tooltip v-if="componentErrors(action.id, component.key).length" type="error">
                  <span v-for="error in componentErrors(action.id, component.key)" :key="error.message">
                    {{ error.message }}
                    <br>
                  </span>
                </help-tooltip>
                <help-tooltip v-if="componentWarnings(action.id, component.key).length" type="warning">
                  <span v-for="warning in componentWarnings(action.id, component.key)" :key="warning.message">
                    {{ warning.message }}
                    <br>
                  </span>
                </help-tooltip>
              </label>            
              <div class="component-options-container">
                <slot :name="`before-options-${action.id}-${component.key}`" />
                <div v-if="component.type === 'RANGE'" class="component-options range-option">
                  <vue-slider
                    :min="component.metadata.min"
                    :max="component.metadata.max"
                    :min-range="component.metadata.minRange / (component.metadata.step || 1)"
                    :interval="component.metadata.step"
                    :marks="!!component.metadata.step"
                    :adsorb="true"
                    :lazy="true"
                    :rail-style="{backgroundColor: 'white'}"
                    :process-style="{backgroundColor: colors[color]}"
                    :dot-style="{backgroundColor: colors[color]}"
                    :dot-options="{focusStyle: {boxShadow: '0 0 8px 0', color: colors[color]}}"
                    :dot-size="35"
                    :height="6"
                    :value="componentOptions(action.id, component.key).allowedRange"
                    :disabled="readonly"
                    @change="range => updateComponentOptions(action.id, component.key, {allowedRange: range})"
                  />
                </div>
                <div v-if="component.type === 'ENUM'" class="component-options">
                  <radio-buttons
                    :options="enums(component)"
                    :multiple="true"
                    :active-color="color"
                    :value="componentOptions(action.id, component.key).availableValues"
                    :disabled="readonly"
                    @input="values => updateComponentOptions(action.id, component.key, {availableValues: values})"
                  />
                </div>
                <div v-if="component.type === 'TOGGLE'" class="component-options">
                  <toggle
                    :on="componentOptions(action.id, component.key).toggled || false"
                    :disabled="readonly"
                    :on-color="color"
                    @toggle="value => updateComponentOptions(action.id, component.key, { toggled: value })"
                  />
                </div>
              </div>
            </div>
            <slot :name="`after-options-${action.id}-${component.key}`" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { keyBy, mapValues, find, fromPairs, uniqBy, forOwn } from 'lodash';
import Vue from 'vue';

import VueSlider from 'vue-slider-component';
import colors from '~lib/common/theme/colors.json';
import RadioButtons from '~lib/common/components/RadioButtons';
import HelpTooltip from '~lib/common/components/HelpTooltip';

import Toggle from '../../common/Toggle';

export default {
  name: 'case-actions',
  components: {
    Toggle,
    VueSlider,
    RadioButtons,
    HelpTooltip,
  },
  props: {
    selectedActions: {
      type: Array,
      required: true,
    },
    availableActions: {
      type: Array,
      required: true,
    },
    errors: {
      type: Object,
      default: () => {},
    },
    warnings: {
      type: Object,
      default: () => {},
    },
    defaultAllSelected: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: 'green-3',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Function,
      default: () => false,
    },
  },
  data() {
    return { colors };
  },
  computed: {
    selectedActionsMap() {
      return keyBy(this.selectedActions, 'id');
    },
    availableActionsMap() {
      return keyBy(this.availableActions, 'id');
    },
  },
  created() {
    if (!this.selectedActions && this.defaultAllSelected) {
      this.update(this.defaults());
    }
  },
  methods: {
    actionErrors(actionId) {
      return (this.errors && this.errors[actionId] &&
        this.errors[actionId].filter(err => !err.metadata.componentId)) || [];
    },
    actionWarnings(actionId) {
      const warnings = (this.warnings && this.warnings[actionId] &&
        this.warnings[actionId].filter(warn => !warn.metadata.componentId)) || [];
      return uniqBy(warnings, 'message');
    },
    componentErrors(actionId, componentId) {
      return (this.errors && this.errors[actionId] &&
        this.errors[actionId].filter(err => err.metadata.componentId === componentId))
        || [];
    },
    componentWarnings(actionId, componentId) {
      const warnings = (this.warnings && this.warnings[actionId] &&
        this.warnings[actionId].filter(warn => warn.metadata.componentId === componentId))
        || [];
      return uniqBy(warnings, 'message');
    },
    defaults() {
      return this.availableActions.map(action => ({
        id: action.id,
        options: {
          enabled: true,
          componentOptions: mapValues(
            keyBy(action.components, 'key'),
            this.defaultComponentOptions,
          ),
        },
      }));
    },
    defaultComponentOptions(component) {
      if (component.type === 'RANGE') {
        return {
          enabled: true,
          allowedRange: [component.metadata.min, component.metadata.max],
        };
      } else if (component.type === 'ENUM') {
        return {
          enabled: true,
          availableValues: [],
        };
      }
      return {};
    },
    isEnabled(actionId) {
      const selectedAction = this.selectedActionsMap[actionId];
      return !!(selectedAction && selectedAction.options.enabled);
    },
    isDisabled(actionId) {
      return this.disabled(actionId, this);
    },
    hasComponents(action) {
      const { components } = action;
      return components && components.length && this.isEnabled(action.id);
    },
    enums(component) {
      return component.metadata.values.map(v => ({
        value: v.key,
        label: v.label.en,
        disabled: v.disabled,
        tooltip: v.tooltip,
      }));
    },
    componentOptions(actionId, componentKey) {
      const selectedAction = this.selectedActionsMap[actionId];
      if (!selectedAction) {
        return null;
      }
      if (!selectedAction.options.enabled) {
        return null;
      }
      if (
        !selectedAction.options.componentOptions ||
        !selectedAction.options.componentOptions[componentKey]
      ) {
        const action = find(this.availableActions, { id: actionId });
        const component = find(action.components, { key: componentKey });
        return this.defaultComponentOptions(component);
      }
      return selectedAction.options.componentOptions[componentKey];
    },
    toggle(actionId) {
      const selectedAction = this.selectedActionsMap[actionId];
      const availableAction = this.availableActionsMap[actionId];
      if (!selectedAction) {
        const componentOptions =
          availableAction.components &&
          fromPairs(
            availableAction.components.map(component => [
              component.key,
              this.defaultComponentOptions(component),
            ]),
          );
        this.update(
          (this.selectedActions || []).concat({
            id: actionId,
            options: {
              enabled: true,
              componentOptions,
            },
          }),
        );
      } else {
        selectedAction.options.enabled = !selectedAction.options.enabled;
        this.update(this.selectedActions);
      }
      this.$forceUpdate();
    },
    updateComponentOptions(actionId, componentKey, updates) {
      const selectedAction = this.selectedActionsMap[actionId];

      selectedAction.options.componentOptions =
        selectedAction.options.componentOptions || {};

      selectedAction.options.componentOptions[componentKey] =
        selectedAction.options.componentOptions[componentKey] || {};

      forOwn(
        updates,
        (value, key) => Vue.set(selectedAction.options.componentOptions[componentKey], key, value),
      );

      this.update(this.selectedActions);
      this.$forceUpdate();
    },
    update(selectedActions) {
      this.$emit(
        'update',
        selectedActions,
      );
    },
  },
};
</script>

<style scoped>
.action {
  margin-top: 30px;
}

.action:first-child {
  margin-top: 0;
}

.action-content {
  display: inline-block;  
}

.action.error .action-content {
  border: solid 2px #f92c2d;
  border-radius: 5px;
  padding: 5px;
}

.action.warning .action-content {
  border: solid 2px #f2994a;
  border-radius: 5px;
  padding: 10px;
  margin-left: -10px;
  margin-right: -10px;
}

label {
  display: block;
  line-height: normal;
  margin-bottom: 2px;
}

.component {
  padding-top: 14px;
  flex: 1 1 100%;
}

.componenent-content {
  display: inline-block;
}

.component.error .componenent-content {
  border: solid 2px #f2994a;
  border-radius: 5px;
  padding: 5px;
  margin-left: -5px;
  margin-right: -5px;
  margin-top: 5px;
}

.component.warning .componenent-content {
  border: solid 2px #f2994a;
  border-radius: 5px;
  padding: 5px;
  margin-left: -5px;
  margin-right: -5px;
  margin-top: 5px;
}

.component label {
  margin-bottom: 5px;
}

.components-container {
  display: flex;
  flex-wrap: wrap;
  border: 2px solid #444444;
  box-sizing: border-box;
  border-radius: 8px;
  padding: 8px 34px 35px;
  margin-top: 13px;
}

.component-options-container {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
}

.component.TOGGLE {
  flex: 1 1 33%;
}
.RANGE .component-options-container .component-options {
  flex: 1;
}

.range-option {
  min-width: 400px;
}

.case-actions >>> .vue-slider .vue-slider-dot {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.case-actions >>> .vue-slider .vue-slider-dot .vue-slider-dot-tooltip {
  visibility: visible;
  transform: none;
  top: auto;
  left: auto;
}
.case-actions >>> .vue-slider .vue-slider-dot .vue-slider-dot-tooltip-inner {
  background: none;
  border: none;
  font-weight: 500;
  font-size: 14px;
}
.case-actions >>>
  .vue-slider
  .vue-slider-dot
  .vue-slider-dot-tooltip-inner-top::after {
  display: none;
}

.case-actions >>> .vue-slider .vue-slider-mark-label {
  display: none;
}
</style>
